import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["menu", "trigger"]

  connect() {
    this.closeOnClickOutside = this.closeOnClickOutside.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)

    // Set initial ARIA attributes
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-haspopup", "menu")
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }

    if (this.hasMenuTarget) {
      this.menuTarget.setAttribute("role", "menu")
      // Generate unique ID if not present
      if (!this.menuTarget.id) {
        this.menuTarget.id = `dropdown-menu-${Math.random().toString(36).substr(2, 9)}`
      }
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-controls", this.menuTarget.id)
      }
    }
  }

  toggle(event) {
    event.stopPropagation()

    if (this.menuTarget.classList.contains("hidden")) {
      this.open()
    } else {
      this.close()
    }
  }

  open() {
    this.menuTarget.classList.remove("hidden")

    // Update ARIA
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "true")
    }

    document.addEventListener("click", this.closeOnClickOutside)
    document.addEventListener("keydown", this.handleKeydown)

    // Focus first menu item
    const firstItem = this.menuTarget.querySelector('[role="menuitem"], a, button')
    if (firstItem) {
      firstItem.focus()
    }
  }

  close() {
    this.menuTarget.classList.add("hidden")

    // Update ARIA
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
    }

    document.removeEventListener("click", this.closeOnClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
  }

  handleKeydown(event) {
    switch (event.key) {
      case "Escape":
        this.close()
        if (this.hasTriggerTarget) {
          this.triggerTarget.focus()
        }
        break
      case "ArrowDown":
        event.preventDefault()
        this.focusNextItem()
        break
      case "ArrowUp":
        event.preventDefault()
        this.focusPreviousItem()
        break
      case "Tab":
        this.close()
        break
    }
  }

  focusNextItem() {
    const items = this.getMenuItems()
    const currentIndex = items.indexOf(document.activeElement)
    const nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0
    items[nextIndex]?.focus()
  }

  focusPreviousItem() {
    const items = this.getMenuItems()
    const currentIndex = items.indexOf(document.activeElement)
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1
    items[prevIndex]?.focus()
  }

  getMenuItems() {
    return Array.from(this.menuTarget.querySelectorAll('[role="menuitem"], a, button:not([disabled])'))
  }

  closeOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.close()
    }
  }

  disconnect() {
    document.removeEventListener("click", this.closeOnClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
  }
}
